'use client';

/**
 * **허브의 전환 띠** — 「여기서 본 것은 한 면이고, 조건을 걸어 파는 곳은 저기 있다」를 **차이의 이름**으로 말한다.
 *
 * ```
 *   ┌ primary.dark 단색 면 · 흰 글자 · 흰 단추 ────────────────────────────────┐
 *   │ 🔍 이 계정이 올렸거나 언급된 모든 소셜 포스팅을 다 확인하고 싶다면?  [Research →] │
 *   │ 더 과거까지 최근 90일 · 한 번에 최대 6,000건 · 내보내기 Excel           │
 *   └────────────────────────────────────────────────────────────────────────┘
 * ```
 *
 * 🔑 **면은 브랜드 파랑 `primary.dark` 단색** — 흰 글자 대비 4.64:1(AA). `primary.main` 은 2.52:1 이라 글자가 안 읽힌다.
 *   단추는 흰 바탕 + `primary.dark` 글자로 면 위에서 가장 먼저 읽힌다. 테두리는 없다.
 *   ⚠️ `primary.darker`→`grey.900` 처럼 검은 남색으로 내려가면 본문보다 앞에 나선다 — 파랑 안에 머문다.
 *   ⚠️ 칸 이름(흰 70%)은 3.06:1 로 AA 미달이다 — 값(흰 100%)이 정보를 지고, 이름은 보조로만 둔다.
 *   palette 의 primary 는 두 모드가 같은 값이라 어두운 모드에서도 같은 파랑이다.
 * 🔑 **문구·칸·가는 곳은 `toHubResearchCopy` 가 면마다 정한다** — 소셜·광고·상품·이벤트 갈래가 저마다 다르다.
 *   여기는 그리기만 한다. 칸은 이름 + 그 값이고 한 줄에 점으로 잇는다(넷까지).
 * ⛔ **허브의 건수를 적지 마라 · 「나머지」를 약속하지 마라** — 허브와 Research 는 **다른 글을 고른다**
 *   (허브는 AI 브랜드 판별 `canonicalBrands`, Research 는 넘겨받은 말을 글자로 찾는다). 머리는 **묻는 꼴**이다.
 * 🚨 **「더 보기」라고 적지 마라** — 같은 것을 더 주는 자리가 아니라 **다른 것을 주는 자리**다.
 */

import { varAlpha } from 'minimal-shared/utils';

import { Box, Stack, Button, Typography } from '@mui/material';

import { RouterLink } from 'src/routes/components';

import { SPACE, RADIUS, TEXT_WRAP } from 'src/theme/ui-tokens';

import { Iconify } from 'src/components/iconify';

import type { HubConversionPoint } from './hub-research-copy';

// ----------------------------------------------------------------------

type Props = {
  /** 머리 한 줄 — 묻는 꼴. 「이 계정이 올렸거나 언급된 모든 소셜 포스팅을 다 확인하고 싶다면?」 */
  title: string;
  points: HubConversionPoint[];
  /** 가는 곳 — 보던 조건을 들고 간다 */
  href: string;
  /** 단추 글자 */
  action: string;
  /** 단추 옆 작은 귀띔 — 「Research 1회 사용」(이번 달 횟수가 정해진 플랜만) */
  hint?: string;
  /** 단추를 눌렀을 때(활동 로그) — 옮기는 것은 `href` 가 한다 */
  onClick?: () => void;
};

export function HubConversionBand({ title, points, href, action, hint, onClick }: Props) {
  return (
    <Box
      sx={{
        p: { xs: 2, md: 2.5 },
        borderRadius: RADIUS.lg,
        bgcolor: 'primary.dark',
        color: 'common.white',
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', md: 'center' }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* 아이콘은 제목 첫 줄의 높이(`1lh`) 안에서 가운데 — 제목이 두 줄로 접혀도 첫 줄에 붙는다 */}
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <Box sx={{ typography: 'uiTitle', height: '1lh', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
              <Iconify icon="solar:magnifer-linear" width={20} sx={{ color: 'common.white' }} />
            </Box>
            <Typography sx={{ typography: 'uiTitle', fontWeight: 700, color: 'common.white', ...TEXT_WRAP.title }}>
              {title}
            </Typography>
          </Stack>

          {/*
            칸은 한 줄 — 이름은 흰 70%, 값은 흰 100%. 좁으면 **칸 단위로** 접힌다.
            🔑 점(·)은 칸 앞에 붙고, 줄 머리에 온 점은 바깥 상자가 잘라 낸다(`ul` 을 점 폭만큼 왼쪽으로 민다) —
              접힌 줄이 점으로 시작하지 않는다.
          */}
          <Box sx={{ mt: 1, overflow: 'hidden' }}>
            <Box
              component="ul"
              sx={{
                m: 0,
                ml: -SPACE.section,
                p: 0,
                listStyle: 'none',
                display: 'flex',
                flexWrap: 'wrap',
                rowGap: 0.5,
                typography: 'uiMeta',
                color: (theme) => varAlpha(theme.vars.palette.common.whiteChannel, 0.7),
              }}
            >
              {points.map((point) => (
                <Box
                  component="li"
                  key={point.label}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'baseline',
                    gap: 0.5,
                    minWidth: 0,
                    wordBreak: 'keep-all',
                    '&::before': {
                      content: '"·"',
                      width: (theme) => theme.spacing(SPACE.section),
                      mr: -0.5,
                      flexShrink: 0,
                      textAlign: 'center',
                      color: (theme) => varAlpha(theme.vars.palette.common.whiteChannel, 0.5),
                    },
                  }}
                >
                  <span>{point.label}</span>
                  <Box component="span" sx={{ color: 'common.white', fontWeight: 600, ...TEXT_WRAP.keep }}>{point.value}</Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        {/* 🔑 흰 단추가 면 위에서 가장 먼저 읽힌다 — 귀띔은 흰 70% 글자로 단추 옆에 */}
        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          flexWrap="wrap"
          alignItems="center"
          sx={{ flexShrink: 0, alignSelf: { xs: 'flex-start', md: 'center' } }}
        >
          {hint ? (
            <Typography
              sx={{ typography: 'uiMeta', color: (theme) => varAlpha(theme.vars.palette.common.whiteChannel, 0.7) }}
            >
              {hint}
            </Typography>
          ) : null}
          <Button
            component={RouterLink}
            href={href}
            onClick={onClick}
            variant="contained"
            endIcon={<Iconify icon="solar:alt-arrow-right-linear" width={16} />}
            sx={{
              bgcolor: 'common.white',
              color: 'primary.dark',
              boxShadow: 'none',
              '&:hover': {
                bgcolor: (theme) => varAlpha(theme.vars.palette.common.whiteChannel, 0.88),
                boxShadow: 'none',
              },
            }}
          >
            {action}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
